import { Outlet, Link as RouterLink, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { AppLogo, BookFab, MobileCogMenu } from '../../common';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { customerAuthActions, useCustomerAuthSlice } from '../../../store/slices/customerAuth';
import { setCustomerToken } from '../../../api/client';

const baseNavLinks = [
  { to: '/', label: 'Home' },
  { to: '/booking', label: 'Book' },
  { to: '/membership', label: 'Membership' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
  { to: '/gallery', label: 'Gallery' },
];

export default function ClientLayout() {
  useCustomerAuthSlice();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const dispatch = useAppDispatch();
  const customer = useAppSelector((s) => s.customerAuth?.user);
  const isBootstrapping = useAppSelector((s) => s.customerAuth?.bootstrapping);

  const navLinks = customer || isBootstrapping
    ? [...baseNavLinks, { to: '/my-bookings', label: 'My bookings' }]
    : baseNavLinks;

  const onLogout = () => {
    dispatch(customerAuthActions.logout());
    setCustomerToken(null);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f8fafc',
      }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: 'linear-gradient(90deg, #0d1b2a 0%, #1b4332 100%)',
          borderBottom: '3px solid #00c853',
        }}
      >
        <Toolbar
          sx={{
            gap: 0.5,
            py: { xs: 0.5, md: 0.75 },
            minHeight: { xs: 56, md: 64 },
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              height: { xs: 48, md: 56 },
              display: 'flex',
              alignItems: 'center',
              mr: 1,
              flexShrink: 0,
              minWidth: 0,
              flexGrow: isMobile ? 1 : 0,
            }}
          >
            <AppLogo variant="navbar" to="/" />
          </Box>

          {!isMobile && (
            <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexGrow: 1, justifyContent: 'flex-end' }}>
              {navLinks.map((link) => {
                const isActive = location.pathname === link.to;
                return (
                  <Button
                    key={link.to}
                    color="inherit"
                    component={RouterLink}
                    to={link.to}
                    sx={{
                      fontWeight: isActive ? 800 : 600,
                      ...(isActive && {
                        bgcolor: 'rgba(0,200,83,0.2)',
                        '&:hover': { bgcolor: 'rgba(0,200,83,0.35)' },
                      }),
                    }}
                  >
                    {link.label}
                  </Button>
                );
              })}
              {isBootstrapping ? (
                <Skeleton variant="rounded" width={96} height={32} sx={{ bgcolor: 'rgba(255,255,255,0.15)' }} />
              ) : customer ? (
                <>
                  <Typography variant="body2" sx={{ alignSelf: 'center', opacity: 0.9, px: 1 }}>
                    Hi, {customer.name.split(' ')[0]}
                  </Typography>
                  <Button color="inherit" onClick={onLogout} sx={{ fontWeight: 600 }}>
                    Sign out
                  </Button>
                </>
              ) : (
                <Button
                  color="inherit"
                  component={RouterLink}
                  to="/login"
                  sx={{ fontWeight: 700, bgcolor: 'rgba(0,200,83,0.15)' }}
                >
                  Sign in
                </Button>
              )}
            </Stack>
          )}

          {isMobile && (
            <Box sx={{ ml: 'auto', flexShrink: 0 }}>
              <MobileCogMenu
                links={
                  customer || isBootstrapping
                    ? navLinks
                    : [...navLinks, { to: '/login', label: 'Sign in', highlight: true }]
                }
                title="The Sports Hub"
                footer={
                  customer ? (
                    <Stack spacing={1.5}>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Hi, {customer.name.split(' ')[0]}
                      </Typography>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="inherit"
                        onClick={onLogout}
                        sx={{ fontWeight: 600, borderColor: 'rgba(255,255,255,0.35)' }}
                      >
                        Sign out
                      </Button>
                    </Stack>
                  ) : undefined
                }
              />
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <Box component="main" sx={{ flex: 1, overflowX: 'hidden' }}>
        <Container sx={{ py: { xs: 2, md: 3 }, px: { xs: 2, sm: 3 } }}>
          <Outlet />
        </Container>
      </Box>
      <BookFab />
      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          background: 'linear-gradient(180deg, #0d1b2a 0%, #1b263b 100%)',
          color: 'rgba(255,255,255,0.85)',
        }}
      >
        <Container>
          <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'center' }}>
            © The Sports Hub — Futsal · Indoor Cricket · Badminton · Pickle Ball
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 0.5, opacity: 0.6 }}>
            Book courts online · Rs. 5 lakh injury insurance · Play your best game
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            sx={{ justifyContent: 'center', mt: 1.5, flexWrap: 'wrap', gap: 1 }}
          >
            <Button
              component={RouterLink}
              to="/terms"
              size="small"
              sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 600, textTransform: 'none', minWidth: 0 }}
            >
              Terms of Service
            </Button>
            <Typography variant="caption" sx={{ opacity: 0.4, alignSelf: 'center' }}>
              ·
            </Typography>
            <Button
              component={RouterLink}
              to="/privacy"
              size="small"
              sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 600, textTransform: 'none', minWidth: 0 }}
            >
              Privacy Policy
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
